import * as React from "react";
import styled from "styled-components";

import { Icons } from "src/library/components/Icon/types";

import {
  InvoiceStatus,
  TransactionStatus
} from "../api/accounting.api";

type StatusVariant = "invoice" | "transaction";
type StatusType = InvoiceStatus | TransactionStatus;
type ThemeColor = "success" | "error" | "warn" | "midGrey" | "primaryLight";

interface AccountingStatusBadgeProps {
  variant: StatusVariant;
  status: StatusType;
  /** Compact mode for table columns - no icon, smaller text */
  compact?: boolean;
}

const STATUS_CONFIG: Record<StatusVariant, {
  icon: Icons;
  colors: Record<string, ThemeColor>;
  labels: Record<string, string>;
}> = {
  invoice: {
    icon: Icons.NOTES,
    colors: {
      paid: "success",
      overdue: "error",
      disputed: "error",
      outstanding: "warn",
      partially_paid: "warn",
      sent: "primaryLight",
      draft: "midGrey",
      written_off: "midGrey",
      cancelled: "midGrey"
    },
    labels: {
      paid: "Paid",
      overdue: "Overdue",
      outstanding: "Outstanding",
      partially_paid: "Partially Paid",
      draft: "Draft",
      sent: "Sent",
      disputed: "Disputed",
      written_off: "Written Off",
      cancelled: "Cancelled"
    }
  },
  transaction: {
    icon: Icons.CREDIT,
    colors: {
      succeeded: "success",
      paid: "success",
      failed: "error",
      overdue: "error",
      canceled: "midGrey",
      processing: "warn",
      requires_payment_method: "warn",
      requires_confirmation: "warn",
      requires_action: "warn",
      requires_capture: "warn"
    },
    labels: {
      succeeded: "Paid",
      paid: "Paid",
      failed: "Failed",
      overdue: "Overdue",
      processing: "Processing",
      canceled: "Cancelled",
      requires_payment_method: "Pending",
      requires_confirmation: "Pending",
      requires_action: "Pending",
      requires_capture: "Pending"
    }
  }
};

const getStatusColor = (variant: StatusVariant, status: StatusType): ThemeColor => {
  return STATUS_CONFIG[ variant ].colors[ status ] || "midGrey";
};

const getStatusLabel = (variant: StatusVariant, status: StatusType): string => {
  return STATUS_CONFIG[ variant ].labels[ status ] || status.charAt(0).toUpperCase() + status.slice(1);
};

const StatusWrapper = styled.div<{ $color: ThemeColor; $compact?: boolean }>`
  display: inline-flex;
  align-items: center;
  width: fit-content;
  gap: ${({ theme, $compact }) => $compact ? 0 : theme.spacing.margins.xs};
  padding: ${({ theme }) => theme.spacing.padding.xs} ${({ theme }) => theme.spacing.padding.sm};
  border-radius: 1rem;
  border: 1px solid ${({ theme, $color }) => theme.colors[ $color ] + "33"};
  background-color: ${({ theme, $color }) => theme.colors[ $color ] + "15"};
`;

const StatusText = styled.span<{ $color: ThemeColor; $compact?: boolean }>`
  font-size: ${({ theme, $compact }) => $compact ? theme.fonts.sizes.sub : theme.fonts.sizes.p};
  font-weight: ${({ theme }) => theme.fonts.weights.bold};
  color: ${({ theme, $color }) => theme.colors[ $color ]};
`;

/**
 * Unified status badge component for accounting entities (invoices and transactions)
 */
const AccountingStatusBadge: React.FC<AccountingStatusBadgeProps> = ({
  variant, status, compact = false
}) => {
  const color = getStatusColor(variant, status);
  const label = getStatusLabel(variant, status);

  return (
    <StatusWrapper
      $color={color}
    >

      <StatusText
        $color={color}
        $compact={compact}
      >
        {label}
      </StatusText>
    </StatusWrapper>
  );
};

export default AccountingStatusBadge;

/**
 * Format invoice status for display (exported for table columns)
 */
export const formatInvoiceStatus = (status: InvoiceStatus): string => {
  return getStatusLabel("invoice", status);
};

/**
 * Format transaction status for display (exported for table columns)
 */
export const formatTransactionStatus = (status: TransactionStatus): string => {
  return getStatusLabel("transaction", status);
};
